import { FC, useContext, useEffect, useState } from "react"
import { Box, Button, Chip, Dialog, Grid, IconButton, Typography, useMediaQuery } from "@material-ui/core";
import { MdClose } from "react-icons/md";
import { PiMedalFill } from "react-icons/pi";

import Lottie from 'lottie-react';

import useStyles from "../Styles/ProjectModalStyles";
import theme from "../../helpers/theme";

import TrophyAnimation from '../../assets/animations/Trophy.json';
import BrainAnimation from '../../assets/animations/Brain.json';
import FailedAnimation from '../../assets/animations/Failed.json';
import { useHistory } from "react-router";
import LearnContext from "../../store/Learn2/LearnContext";
import PoolContext from "../../store/Pool/PoolContext";
import LearnSocialSection from "./LearnSocialSection";
import { QuizStatuses } from "../../store/Learn2/learn.interface";

export type QuizState = 'quizSuccess' | 'quizFail' | 'poolQuizFail' | 'whitelistCompleted' | null;

type LearnProgressCardsProps = {
    open: boolean;
    setOpen: (open: boolean) => void;
    state: QuizState | null;
    attempts: number | null;
    tickets: number | null;
    next: (event: any, reason?: string) => void;
    projectName: string;
    socialLink: string;
    projectImage: any;
    quizStatus?: QuizStatuses
    [key: string]: any
}

enum QuizTitle {
    Success = 'Kudos !',
    Congrats = 'Congratulations',
    Retry = 'Unfortunate',
    Failed = 'Quiz Failed',
}

enum QuizMessage {
    Success = 'You have successfully completed the quiz. Please continue with the next one!',
    Retry = "Don't give up now! You have more attempts to get whitelisted for the project.",
    Failed = 'You failed way too many times :( Be on the lookout for new quizzes!',
    Whitelisted = 'Congratulations! You have been successfully whitelisted for this project!',
}

const LearnProgressCards: FC<any> = ({ open, setOpen, state, attempts, tickets, next, projectName, socialLink, projectImage, quizStatus }: LearnProgressCardsProps) => {

    const classes = useStyles();
    const history = useHistory();

    const { resetLearnState } = useContext<any>(LearnContext);
    
    const [selectedAnimation, setAnimation] = useState<any>();

    useEffect(() => {
        switch(state) {
            case 'quizSuccess':
                setAnimation(TrophyAnimation);
                break;
            case 'poolQuizFail':
                setAnimation(FailedAnimation);
                break;
            case 'quizFail':
                setAnimation(BrainAnimation);
        }
    }, [state])

    const handleClose = (event: React.SyntheticEvent<any, Event>, reason: string) => {
        if (reason === 'backdropClick') return;
        setOpen(false);
    }

    const handleFinish = (event: React.SyntheticEvent<any, Event>) => {
        history.push('/deals');
        resetLearnState();
    }

    return (
        <>
            <Box component="section">
                <Dialog
                    open={open}
                    onClose={handleClose}
                    aria-aria-labelledby="learn-progress"
                    aria-aria-describedby="learn-progress-result"
                    className={classes.modalpages}
                    disableEscapeKeyDown={true}
                >
                    <Grid
                        container
                        style={{
                            padding: "20px",
                            display: "block"
                        }}
                        direction="column"
                    >
                        <Grid
                            item
                        >
                            <Grid
                                container
                                direction="row"
                                justifyContent="space-between"
                                alignItems="flex-start"
                            >
                                <Grid 
                                    item
                                    lg={8}
                                    className={classes.learnProgressAttempts}
                                >
                                    {
                                        state === 'quizFail' && attempts && (
                                            <Typography variant="body1">
                                                Attempts Left:
                                                <span>
                                                    &nbsp;{attempts}
                                                </span>
                                            </Typography>
                                        )
                                    }
                                    {
                                        tickets && state === 'whitelistCompleted' && (
                                            <Typography variant="body1">
                                                Reward:&nbsp;
                                                <Chip 
                                                    variant="outlined"
                                                    icon={<PiMedalFill className={classes.chip} />}
                                                    label={
                                                        <Typography variant="body1">
                                                            &nbsp;<span>{tickets} </span>
                                                            Tickets
                                                        </Typography>
                                                    }
                                                />
                                            </Typography>
                                        )
                                    }
                                </Grid>
                                {
                                    state !== 'whitelistCompleted' && (
                                        <Grid
                                            item
                                        >
                                            <IconButton
                                                edge="end"
                                                color="inherit"
                                                aria-label="close"
                                                onClick={(event) => handleClose(event, 'buttonClick')}
                                            >
                                                <MdClose />
                                            </IconButton>
                                        </Grid>
                                    )
                                }
                            </Grid>
                        </Grid>
                        <Grid 
                            item
                            lg={12}
                            style={{
                                display: "flex",
                                justifyContent: "center",
                            }}
                        >
                            {
                                state !== 'whitelistCompleted' && (
                                    <Lottie animationData={selectedAnimation} style={{
                                        width: "180px",
                                        height: "auto",
                                    }} />
                                )
                            }
                        </Grid>
                        <Box 
                            display='flex'
                            justifyContent="center"
                            alignItems="center"
                            flexDirection='column'
                        >
                            <Box>
                                <Typography variant="h3" className={classes.progressTitle}>
                                    {
                                        state === 'quizSuccess'
                                            ? QuizTitle.Success
                                            : state === 'quizFail'
                                                ? QuizTitle.Retry
                                                : state === 'poolQuizFail'
                                                    ? QuizTitle.Failed
                                                    : QuizTitle.Congrats
                                    }
                                </Typography>
                            </Box>
                            {quizStatus === QuizStatuses.EXPIRED && <Box>
                                <Typography variant="h5" color='error'>
                                    Quiz Timed Out
                                </Typography>
                            </Box>}
                            <Grid item lg={9}>
                                <Typography variant="body1" className={classes.progressMessage}>
                                    {
                                        state === 'quizSuccess'
                                            ? QuizMessage.Success
                                            : state === 'quizFail'
                                                ? QuizMessage.Retry
                                                : state === 'poolQuizFail'
                                                    ? QuizMessage.Failed
                                                    : state === 'whitelistCompleted'
                                                        ? QuizMessage.Whitelisted
                                                        : ''
                                    }
                                </Typography>
                            </Grid>
                        </Box>
                        <Grid 
                            item
                            lg={12}
                            style={{
                                display: "flex",
                                justifyContent: "center",
                            }}
                        >
                            {
                                state === 'whitelistCompleted' && <LearnSocialSection projectImage={projectImage} projectName={projectName} socialLink={socialLink} />
                            }
                        </Grid>
                        <Grid
                            item
                        >
                            <Grid 
                                container
                                justifyContent="center"
                                lg={12}
                                direction="row"
                                style={{
                                    marginTop: "20px",
                                }}
                                spacing={2}
                            >
                                {
                                    (state === 'poolQuizFail' || state === 'whitelistCompleted') 
                                    ?
                                        (
                                            <Grid item lg={10} xs={10} md={10} sm={10}>
                                                <Button variant="contained" color="primary" fullWidth onClick={(e) => handleFinish(e)}>
                                                    Return to Home
                                                </Button>
                                            </Grid>
                                        )

                                    : state === 'quizSuccess'
                                        ?
                                        (
                                            <Grid item lg={10} xs={10} md={10} sm={10}>
                                                <Button variant="contained" color="primary" fullWidth onClick={(e) => next(e, 'buttonClick')}>
                                                    Next Quiz
                                                </Button>
                                            </Grid>
                                        )
                                        : state === 'quizFail'
                                            ?
                                                (
                                                    <Grid item lg={10} xs={10} md={10} sm={10}>
                                                        <Button variant="outlined" color="secondary" fullWidth onClick={(e) => handleClose(e, 'buttonClick')}>
                                                                Close
                                                        </Button>
                                                    </Grid>
                                                )
                                            :   ''
                                }
                            </Grid>
                        </Grid>
                    </Grid>
                </Dialog>
            </Box>
        </>
    )
}

export default LearnProgressCards;